import { Edit } from '@mui/icons-material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { ICourtCourtRole } from '../../../Interfaces/IAemter';
import { IDocument } from '../../../Interfaces/IDocument';
import { useLegalDependency } from '../../LegalDependencyContext';
import {
  ILegalRequirement,
  ILegalRequirementFullObject,
} from '../../../Interfaces/ILegalRequirement';
import { ILegalRequirementDeadline } from '../../../Interfaces/ILegalRequirement_deadlines';
import { ILegalPhaseFullObject } from '../../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../../Interfaces/ILegalRequirementState';
import { TextModuleSelector } from '../../../TextModule/TextModuleSelector';
import { CustomEditor } from '../../../core/CustomEditor';
import { FullScreenDialog } from '../../../core/FullScreenDialog';
import { SectionNavLayout } from '../../../core/SectionNavLayout';
import { AddPrerequisiteDeadline } from './AddPrerequisiteDeadline';
import { LegalRequirementAddCourt } from './LegalRequirementAddCourt';
import { LegalRequirementDeadlineTable } from './LegalRequirementDeadlineTable';
import { DocumentOverview } from '../../../Document/DocumentOverview';
import { CustomeTextField } from '../../../generic/CustomeTextField';
import { RequirementResponsibilityCard } from './RequirementResponsibilityCard';
import { CollapsibleSection } from '../../../core/CollapsibleSection';
import { LegalRequirementDependencyUsage } from './LegalRequirementDependencyUsage';
import { LegalDependencyMockup } from '../LegalDependencyMockup';
import { TaskAutomationManager } from '../../../LegalTransactionType/TaskAutomationManager';
import { KnowledgeItemManager } from '../../../LegalTransactionType/KnowledgeItemManager';

interface IProps {
  currentRequirement: ILegalRequirementFullObject;
  setCurrentRequirement: Function;
  courtcourtroleArray: ICourtCourtRole[];
  legaltransactionId: number;
  currentLegalPhases: ILegalPhaseFullObject[];
  legalRequirementStates: ILegalRequirementState[];
}

export const EditLegalRequirementData: React.FC<IProps> = ({
  currentRequirement,
  setCurrentRequirement,
  courtcourtroleArray,
  legaltransactionId,
  currentLegalPhases,
  legalRequirementStates,
}) => {
  const { legalDependencyData, setLegalDependencyData } = useLegalDependency();
  // useStates
  const isSendEmailPossible =
    currentRequirement.sendMail || currentRequirement.isInterneLegalRequirement;

  const [currentEmployeesDocumentArray, setCurrentEmployeesDocumentArray] =
    useState<IDocument[]>([]);

  useEffect(() => {
    const updatedArray = currentRequirement.DocumentArrayEmployees
      ? currentRequirement.DocumentArrayEmployees.map(doc => ({
          ...doc,
          perEmail: isSendEmailPossible ? doc.perEmail : false,
        }))
      : [];

    setCurrentEmployeesDocumentArray(updatedArray);
  }, [isSendEmailPossible]);

  const [dependencyEditorOpen, setDependencyEditorOpen] = useState(false);
  const [selectedDependencyRuleId, setSelectedDependencyRuleId] = useState<
    number | null
  >(null);
  const [courtcourtroleDialogOpen, setCourtcourtroleDialogOpen] =
    useState(false);
  const [addPrerequisteDeadlineOpen, setAddPrerequisteDeadlineOpen] =
    useState(false);
  //
  const handleChangePrerequisteDeadline = useCallback(
    (changedPrequiste: ILegalRequirementDeadline) => {
      const newArray = currentRequirement.DeadlineArray
        ? currentRequirement.DeadlineArray
        : [];

      if (changedPrequiste.idDeadline === -1) {
        changedPrequiste.idDeadline = Math.floor(Math.random() * 1000000);
        newArray.push(changedPrequiste);
      } else {
        const index = newArray.findIndex(
          x => x.idDeadline === changedPrequiste.idDeadline
        );
        if (index === -1) {
          newArray.push(changedPrequiste);
        } else {
          newArray[index] = changedPrequiste;
        }
      }
      setCurrentRequirement({
        ...currentRequirement,
        DeadlineArray: newArray,
      });
    },
    [
      currentRequirement,
      setCurrentRequirement,
      courtcourtroleArray,
      legaltransactionId,
    ]
  );

  const handleDeleteDeadline = useCallback(
    (index: number) => {
      const newArray = currentRequirement.DeadlineArray
        ? currentRequirement.DeadlineArray
        : [];
      newArray.splice(index, 1);
      setCurrentRequirement({
        ...currentRequirement,
        DeadlineArray: newArray,
      });
    },
    [
      currentRequirement,
      setCurrentRequirement,
      courtcourtroleArray,
      legaltransactionId,
    ]
  );
  // useEffects
  useEffect(() => {
    setCurrentRequirement({
      ...currentRequirement,
      DocumentArrayEmployees: currentEmployeesDocumentArray,
    });
  }, [currentEmployeesDocumentArray]);

  // === Kopfbereich (#1244): Grunddaten oben (wie im Vorgang) ===
  const headerContent = (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
      <Grid container spacing={2} alignItems='center'>
        <Grid item sm={2}>
          <TextField
            label='interne Maßnahme'
            value={
              currentRequirement.isInterneLegalRequirement ? 'true' : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirement({
                ...currentRequirement,
                isInterneLegalRequirement: event.target.value === 'true',
                sendMail:
                  event.target.value === 'true'
                    ? false
                    : currentRequirement.sendMail,
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
        <Grid item sm={6}>
          <TextField
            label='Bedingung'
            value={currentRequirement.LegalRequirement}
            error={currentRequirement.LegalRequirement === ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirement({
                ...currentRequirement,
                LegalRequirement: event.target.value,
              })
            }
            size='small'
            fullWidth
          />
        </Grid>
        <Grid item sm={2}>
          <TextField
            label='MA lädt Dateien hoch'
            value={
              currentRequirement.canUploadFilesEmployees ? 'true' : 'false'
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCurrentRequirement({
                ...currentRequirement,
                canUploadFilesEmployees: event.target.value === 'true',
                DocumentArray:
                  event.target.value === 'false'
                    ? []
                    : currentRequirement.DocumentArrayEmployees,
              });
              setCurrentEmployeesDocumentArray(
                event.target.value === 'false'
                  ? []
                  : currentEmployeesDocumentArray
              );
            }}
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
        <Grid item sm={2}>
          <TextField
            label='Mandant lädt Dateien hoch'
            value={currentRequirement.canUploadFilesClient ? 'true' : 'false'}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentRequirement({
                ...currentRequirement,
                canUploadFilesClient: event.target.value === 'true',
              })
            }
            size='small'
            fullWidth
            select
          >
            <MenuItem value='true'>Ja</MenuItem>
            <MenuItem value='false'>Nein</MenuItem>
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );

  // === Reiter-Inhalte (#1244) — Logik unverändert, nur gruppiert ===
  const allgemeinContent = (
    <Grid container spacing={2}>
      {currentRequirement.canUploadFilesEmployees && (
        <Grid mt={6} item sm={12}>
          <DocumentOverview
            key={`DocumentOverview-employees`}
            documentArray={currentEmployeesDocumentArray}
            setDocumentArray={setCurrentEmployeesDocumentArray}
            allowRemove={true}
            allowUpload={true}
            title='Für den Mandanten bereitgestellte Daten'
            isStandarddocument={true}
            isSendEmailPossible={isSendEmailPossible}
          />
        </Grid>
      )}

      <Grid item sm={12}>
        <TextModuleSelector
          key={`transaction-legalrequirementedit`}
          floatRight
          idTextModuleCategory={4}
          txt={currentRequirement.InfoText}
          setTxt={(txt: string) =>
            setCurrentRequirement({
              ...currentRequirement,
              InfoText: txt,
            })
          }
        />
        <TextField
          label='Infotext'
          value={
            currentRequirement.InfoText === null
              ? ''
              : currentRequirement.InfoText
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirement({
              ...currentRequirement,
              InfoText: event.target.value === '' ? null : event.target.value,
            })
          }
          size='small'
          fullWidth
          multiline
          rows={3}
        />
      </Grid>

      {/* Fehler-Konfiguration */}
      <Grid item sm={6}>
        <TextField
          label='Fehler möglich'
          value={currentRequirement.hasError ? 1 : 2}
          onChange={({
            target: { value },
          }: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirement({
              ...currentRequirement,
              hasError: Number(value) === 1,
            })
          }
          size='small'
          fullWidth
          select
        >
          <MenuItem value={1}>Ja</MenuItem>
          <MenuItem value={2}>Nein</MenuItem>
        </TextField>
      </Grid>
      <Grid item sm={6}>
        <TextField
          label='Freitext bei Fehler erlauben'
          value={currentRequirement.enableFreeTextError ? 1 : 2}
          onChange={({
            target: { value },
          }: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirement({
              ...currentRequirement,
              enableFreeTextError: Number(value) === 1,
            })
          }
          size='small'
          fullWidth
          select
        >
          <MenuItem value={1}>Ja</MenuItem>
          <MenuItem value={2}>Nein</MenuItem>
        </TextField>
      </Grid>
      <Grid item sm={12}>
        <TextField
          label='Fehlermeldung'
          value={currentRequirement.TextOfError}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirement({
              ...currentRequirement,
              TextOfError: event.target.value,
            })
          }
          size='small'
          fullWidth
          multiline
          rows={3}
        />
      </Grid>
    </Grid>
  );

  const emailContent = (
    <Grid container spacing={2}>
      <Grid item sm={4}>
        <TextField
          label='Auto. E-Mail versenden (wenn erfüllt)'
          value={currentRequirement.sendMail ? 'true' : 'false'}
          disabled={currentRequirement.isInterneLegalRequirement}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentRequirement({
              ...currentRequirement,
              sendMail: event.target.value === 'true',
            })
          }
          size='small'
          fullWidth
          select
        >
          <MenuItem value='true'>Ja</MenuItem>
          <MenuItem value='false'>Nein</MenuItem>
        </TextField>
      </Grid>

      <Grid item sm={8}>
        <CustomeTextField
          label='E-Mail Betreff'
          attr='EmailSubject'
          object={currentRequirement}
          setObject={setCurrentRequirement}
          type='string'
          size='small'
          maxLength='md'
          error={
            currentRequirement.sendMail &&
            currentRequirement.EmailSubject === ''
          }
          helperText={
            currentRequirement.sendMail &&
            currentRequirement.EmailSubject === ''
              ? 'E-Mail Betreff ist erforderlich'
              : undefined
          }
        />
      </Grid>

      <Grid item sm={12}>
        <CustomEditor
          txt={
            currentRequirement.EmailBody === null
              ? ''
              : currentRequirement.EmailBody
          }
          setTxt={(txt: string) =>
            setCurrentRequirement({
              ...currentRequirement,
              EmailBody: txt === '' ? null : txt,
            })
          }
          idTextModuleCategory={3}
          dictonaryIdsArray={[1, 2, 3]}
        />
      </Grid>
    </Grid>
  );

  const zustaendigkeitContent = (
    <RequirementResponsibilityCard
      mode='requirement'
      value={currentRequirement}
      onChange={next =>
        setCurrentRequirement({
          ...currentRequirement,
          ...next,
        } as ILegalRequirementFullObject)
      }
      onOpenManualCourt={() => setCourtcourtroleDialogOpen(true)}
      idLegalRequirement={currentRequirement.idLegalRequirement}
    />
  );

  const startbedingungenContent = (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <CollapsibleSection
          title='Startbedingungen'
          subtitle={
            currentRequirement.DeadlineArray &&
            currentRequirement.DeadlineArray.length > 0
              ? `${currentRequirement.DeadlineArray.length} Bedingung(en) hinterlegt`
              : 'Wann diese Maßnahme starten darf — optional'
          }
          icon={<PlaylistAddCheckIcon color='action' />}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} display='flex' justifyContent='flex-end'>
              <Tooltip title='Bearbeiten Sie die Startbedingungen'>
                <IconButton
                  onClick={() => setAddPrerequisteDeadlineOpen(true)}
                  size='small'
                >
                  <Edit fontSize='small' />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <LegalRequirementDeadlineTable
                deadlineArray={currentRequirement.DeadlineArray}
                setDeadline={handleChangePrerequisteDeadline}
                deleteDeadline={handleDeleteDeadline}
              />
            </Grid>
          </Grid>
        </CollapsibleSection>
      </Grid>

      <Grid item sm={12}>
        <LegalRequirementDependencyUsage
          idLegalRequirement={currentRequirement.idLegalRequirement}
          currentLegalTransactionId={legaltransactionId}
          onOpenDependencyEditor={() => {
            setSelectedDependencyRuleId(null);
            setDependencyEditorOpen(true);
          }}
          onEditRule={ruleId => {
            setSelectedDependencyRuleId(ruleId);
            setDependencyEditorOpen(true);
          }}
        />
      </Grid>
    </Grid>
  );

  const isRequirementDone = currentRequirement.idLegalRequirementState === 40;
  const aufgabenContent = (
    <TaskAutomationManager
      items={currentRequirement.TaskAutomations ?? []}
      onChange={next =>
        setCurrentRequirement({
          ...currentRequirement,
          TaskAutomations: next,
        } as ILegalRequirementFullObject)
      }
      legalRequirementStates={legalRequirementStates}
      locked={isRequirementDone}
    />
  );

  const wissenContent = (
    <KnowledgeItemManager
      items={currentRequirement.KnowledgeItems ?? []}
      onChange={next =>
        setCurrentRequirement({
          ...currentRequirement,
          KnowledgeItems: next,
        } as ILegalRequirementFullObject)
      }
      inheritedItems={currentRequirement.InheritedKnowledgeItems ?? []}
      locked={isRequirementDone}
    />
  );

  return (
    <Box sx={{ m: 2 }}>
      <FullScreenDialog
        isOpen={dependencyEditorOpen}
        onClose={() => setDependencyEditorOpen(false)}
        title='Abhängigkeiten zwischen Maßnahmen'
        disablePadding={true}
      >
        <LegalDependencyMockup
          currentLegalTransactionId={legaltransactionId}
          currentLegalPhases={currentLegalPhases}
          initialSelectedRuleId={selectedDependencyRuleId}
          legalRequirementStates={legalRequirementStates}
          legalDependencyData={legalDependencyData}
          setLegalDependencyData={setLegalDependencyData}
        />
      </FullScreenDialog>

      <SectionNavLayout
        header={headerContent}
        sections={[
          {
            key: 'allgemein',
            label: 'Allgemein',
            icon: <ListAltIcon fontSize='small' />,
            content: allgemeinContent,
          },
          {
            key: 'startbedingungen',
            label: 'Startbedingungen',
            icon: <PlaylistAddCheckIcon fontSize='small' />,
            content: startbedingungenContent,
          },
          {
            key: 'email',
            label: 'E-Mail',
            icon: <EmailIcon fontSize='small' />,
            content: emailContent,
          },
          {
            key: 'zustaendigkeit',
            label: 'Bearbeitung & Zuständigkeit',
            icon: <AssignmentIndIcon fontSize='small' />,
            content: zustaendigkeitContent,
          },
          {
            key: 'aufgaben',
            label: 'Aufgaben',
            icon: <ChecklistIcon fontSize='small' />,
            content: aufgabenContent,
          },
          {
            key: 'wissen',
            label: 'Wissensergänzung',
            icon: <PsychologyIcon fontSize='small' />,
            content: wissenContent,
          },
        ]}
      />

      <AddPrerequisiteDeadline
        open={addPrerequisteDeadlineOpen}
        currentLegalRequirement={currentRequirement}
        setDeadline={handleChangePrerequisteDeadline}
        onClose={() => setAddPrerequisteDeadlineOpen(false)}
        legaltransactionId={legaltransactionId}
      ></AddPrerequisiteDeadline>

      <LegalRequirementAddCourt
        legalRequirement={currentRequirement}
        courtCourtRoleArray={courtcourtroleArray}
        open={courtcourtroleDialogOpen}
        onSave={(newLegalRequirement: ILegalRequirement) => {
          setCurrentRequirement(newLegalRequirement);
          setCourtcourtroleDialogOpen(false);
        }}
        onClose={() => setCourtcourtroleDialogOpen(false)}
      ></LegalRequirementAddCourt>
    </Box>
  );
};
